import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError, authQuery, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CompassLogo } from "../components/CompassLogo";
import SignaturePad from "../components/SignaturePad";
import { ChevronDown, Eye, Phone, User, BarChart3, Upload, X, Stamp as StampIcon } from "lucide-react";

function AccordionSection({ icon: Icon, title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="lodge-acc-item">
      <button type="button" className="lodge-acc-trigger" onClick={() => setOpen(!open)}>
        <span className="lodge-acc-trigger-left">
          <Icon size={15} />
          <span>{title}</span>
        </span>
        <ChevronDown size={16} style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 200ms" }} color="#858481" />
      </button>
      {open && <div className="lodge-acc-content">{children}</div>}
    </div>
  );
}

export default function LodgeInfoPage() {
  const { t, i18n } = useTranslation();
  const INFO_FIELDS = [
    ["name", t("lodgeInfo.lodgeName")],
    ["number", t("lodgeInfo.number")],
    ["obedience", t("lodgeInfo.obedience")],
    ["city", t("lodgeInfo.city")],
    ["country", t("lodgeInfo.country")],
    ["jurisdiction", t("recognized.jurisdiction")],
    ["year_opened", t("lodgeInfo.yearOpened")],
    ["founded", t("recognized.founded")],
    ["meeting", t("recognized.meeting")],
  ];
  const CONTACT_FIELDS = [
    ["address", t("lodgeInfo.address")],
    ["email", t("lodgeInfo.email")],
    ["phone", t("lodgeInfo.phone")],
    ["website", t("lodgeInfo.website")],
  ];
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [lodge, setLodge] = useState(null);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);

  // When the tenant is an obedience (Grand Lodge), surface the 7 Grand-Lodge
  // officer slots. For regular lodges we keep the symbolic 5-officer layout.
  // `is_obedience` is flagged on `db.lodge` at creation by the super-admin
  // tenant-creation endpoint, with a one-time migration for legacy records.
  //
  // BACKWARD-COMPAT: if the flag was never set on legacy obediences (older
  // production records), but the document already carries any populated
  // `mare_*` officer field, auto-treat it as an obedience so the Grand
  // Officers are NOT silently hidden in the UI.
  // Canonical Grand-Lodge officer order (per Obedience standards).
  // Backward-compat: any populated `mare_*` field still flips the page
  // into obedience mode even if `is_obedience` was never explicitly set
  // on legacy rows.
  const hasAnyGrandOfficer = lodge && [
    "mare_maestru",
    "mare_maestru_adjunct",
    "mare_prim_supraveghetor",
    "mare_al_doilea_supraveghetor",
    "mare_secretar",
    "mare_trezorier",
    "mare_cancelar",
    "mare_orator",
    "mare_expert",
    "mare_dir_ceremonii",
    "mare_filantrop",
    "mare_acoperitor",
  ].some((k) => (lodge[k] || "").trim());
  const isObedience = !!lodge?.is_obedience || hasAnyGrandOfficer;
  const OFFICER_FIELDS = isObedience
    ? [
        ["mare_maestru", t("lodgeInfo.officerGM")],
        ["mare_maestru_adjunct", t("lodgeInfo.officerDGM")],
        ["mare_prim_supraveghetor", t("lodgeInfo.officerGSW")],
        ["mare_al_doilea_supraveghetor", t("lodgeInfo.officerGJW")],
        ["mare_secretar", t("lodgeInfo.officerGSec")],
        ["mare_trezorier", t("lodgeInfo.officerGTres")],
        ["mare_orator", t("lodgeInfo.officerGOrator")],
        ["mare_cancelar", t("lodgeInfo.officerGChan")],
        ["mare_expert", t("lodgeInfo.officerGExpert")],
        ["mare_dir_ceremonii", t("lodgeInfo.officerGDirCer")],
        ["mare_filantrop", t("lodgeInfo.officerGAlm")],
        ["mare_acoperitor", t("lodgeInfo.officerGInnerG")],
      ]
    : [
        ["venerabil_maestru", t("lodgeInfo.officerWM")],
        ["prim_supraveghetor", t("lodgeInfo.officerSW")],
        ["al_doilea_supraveghetor", t("lodgeInfo.officerJW")],
        ["secretar", t("lodgeInfo.officerSec")],
        ["trezorierul", t("lodgeInfo.officerTres")],
      ];

  // When the active context is a sub-lodge (sovereign jurisdiction lodge),
  // load/save against `jurisdiction-lodges/{id}` so the sub-lodge has its
  // own profile (officers, logo, stamp, signatures). Otherwise, load the
  // obedience profile from `/api/lodge`.
  const childLodgeId = user?.child_lodge_id;

  const load = async () => {
    try {
      if (childLodgeId) {
        const [allLodges, s] = await Promise.all([
          api.get("/jurisdiction-lodges"),
          api.get("/stats"),
        ]);
        const own = (allLodges.data || []).find((x) => x.id === childLodgeId);
        if (!own) {
          setError(t("lodgeInfo.notFound", "Lodge not found"));
          return;
        }
        // Sub-lodges are not obediences — render the 5-officer layout.
        setLodge({ ...own, is_obedience: false });
        setForm({ ...own, is_obedience: false });
        setStats(s.data);
      } else {
        const [l, s] = await Promise.all([api.get("/lodge"), api.get("/stats")]);
        setLodge(l.data); setForm(l.data); setStats(s.data);
      }
    } catch (e) { setError(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [childLodgeId]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      if (childLodgeId) {
        await api.patch(`/jurisdiction-lodges/${childLodgeId}`, form);
      } else {
        await api.put("/lodge", form);
      }
      setEditing(false);
      await load();
      toast.success(t("lodgeInfo.savedSuccess"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.storage_path }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploading(false); }
  };

  // Upload + auto-vectorise the lodge stamp/seal. The backend strips the
  // white background and saves a transparent PNG; here we just pump the
  // file through and store the resulting path (relative to /api/photo).
  const [uploadingStamp, setUploadingStamp] = useState(false);
  const uploadStamp = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingStamp(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/stamp", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // `storage_path` is e.g. "square-compass/stamps/<uuid>.png" — same
      // shape as logo_url so the existing /api/photo proxy serves it.
      setForm((f) => ({ ...f, stamp_url: data.storage_path }));
      toast.success(t("lodgeInfo.stamp_vectorised_and_saved"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploadingStamp(false); }
  };

  const removeStamp = () => {
    if (!window.confirm(t("lodgeInfo.remove_the_lodge_stamp"))) return;
    setForm((f) => ({ ...f, stamp_url: null }));
  };

  // ─── Sovereign sub-lodge branding ────────────────────────────────
  // Stand-alone uploads for the WM/Secretary signatures of a child
  // jurisdiction lodge. They save IMMEDIATELY via PATCH (independent of
  // the edit modal) so the brand identity is editable in one click.
  const [savingBrand, setSavingBrand] = useState(null); // 'stamp'|'wm'|'sec'|null
  const [sigPad, setSigPad] = useState(null); // 'wm' | 'sec' | null

  const persistBrand = async (patch) => {
    if (!childLodgeId) return;
    try {
      await api.patch(`/jurisdiction-lodges/${childLodgeId}`, patch);
      await load();
      toast.success(t("lodgeInfo.savedSuccess"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadBrandStamp = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSavingBrand("stamp");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/stamp", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await persistBrand({ stamp_url: data.storage_path });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSavingBrand(null); }
  };

  const uploadBrandSignatureFile = async (kind, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSavingBrand(kind);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/lodge-signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const field = kind === "wm" ? "wm_signature_url" : "sec_signature_url";
      await persistBrand({ [field]: data.storage_path });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSavingBrand(null); }
  };

  const saveDrawnSignature = async (kind, dataUrl) => {
    setSavingBrand(kind);
    setSigPad(null);
    try {
      const { data } = await api.post("/upload/lodge-signature/digital", { png_base64: dataUrl });
      const field = kind === "wm" ? "wm_signature_url" : "sec_signature_url";
      await persistBrand({ [field]: data.storage_path });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSavingBrand(null); }
  };

  const removeBrandSignature = async (kind) => {
    if (!window.confirm(t("lodgeInfo.removeSignature"))) return;
    const field = kind === "wm" ? "wm_signature_url" : "sec_signature_url";
    await persistBrand({ [field]: null });
  };

  const removeBrandStamp = async () => {
    if (!window.confirm(t("lodgeInfo.remove_the_lodge_stamp"))) return;
    await persistBrand({ stamp_url: null });
  };

  if (!lodge) return <div className="spinner" />;

  return (
    <div data-testid="lodge-info-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="page-title">{t("lodgeInfo.title")}</h1>
        {isAdmin && !editing && (
          <div className="flex items-center gap-2">
            {/* Show a "Mark as Obedience" button when the auto-detect
                heuristic has flagged the tenant as one (because of Grand
                officers populated) but the explicit DB flag is still
                missing — usually a legacy production record. One-click
                fix that writes `is_obedience=True` permanently. */}
            {!childLodgeId && !lodge.is_obedience && hasAnyGrandOfficer && (
              <button
                className="btn-outline-gold"
                onClick={async () => {
                  if (!window.confirm(t(
                    "lodgeInfo.markObedienceConfirm",
                    "Confirmi că această lojă este o Obediență (Marea Lojă)? Va fi marcată permanent și se vor afișa cei 7 Mari Ofițeri."
                  ))) return;
                  try {
                    await api.post("/lodge/mark-obedience");
                    toast.success(t("lodgeInfo.markObedienceDone", "Tenant marcat ca Obediență."));
                    await load();
                  } catch (e) {
                    toast.error(formatApiError(e));
                  }
                }}
                data-testid="mark-obedience-btn"
              >
                {t("lodgeInfo.markObedience", "MARCHEAZĂ CA OBEDIENȚĂ")}
              </button>
            )}
            <button
              className="btn-outline-gold"
              onClick={async () => {
                try {
                  const preview = await api.post("/admin/passport-numbers/migrate-lodge-country?dry_run=true");
                  const p = preview.data;
                  if (!p.stock_renamed && !p.members_updated) {
                    toast.success(t("lodgeInfo.passportMigrateNoop", "Pașapoartele au deja codul corect ({{iso}}).", { iso: p.expected_iso }));
                    return;
                  }
                  const ok = window.confirm(
                    t("lodgeInfo.passportMigrateConfirm",
                      "Vom redenumi {{stock}} pașapoarte din stoc și {{members}} pașapoarte asignate membrilor în prefixul {{iso}}. Continuăm?",
                      { stock: p.stock_renamed, members: p.members_updated, iso: p.expected_iso })
                  );
                  if (!ok) return;
                  const { data } = await api.post("/admin/passport-numbers/migrate-lodge-country?dry_run=false");
                  toast.success(t("lodgeInfo.passportMigrateDone",
                    "Migrat: {{stock}} stoc + {{members}} membri → {{iso}}.",
                    { stock: data.stock_renamed, members: data.members_updated, iso: data.expected_iso }));
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
              data-testid="migrate-passport-country-btn"
              title={t("lodgeInfo.passportMigrateHelp", "Corectează prefixul pașapoartelor pentru țara acestei loji")}
            >
              {t("lodgeInfo.passportMigrate", "MIGREAZĂ PAȘAPOARTE")}
            </button>
            <button className="lodge-edit-btn-gold" onClick={() => setEditing(true)} data-testid="edit-lodge-btn">✎ {t("common.edit")}</button>
          </div>
        )}
      </div>

      <div className="gold-divider my-8" />

      {error && <div className="error-banner mb-4">{error}</div>}

      {/* Hero card */}
      <div className="lodge-hero">
        {lodge.logo_url ? (
          <img src={`${API}/photo?path=${encodeURIComponent(lodge.logo_url)}${authQuery().replace("?", "&")}`} alt="" style={{ width: 70, height: 70, objectFit: "cover", border: "1px solid #c9a84c" }} />
        ) : (
          <CompassLogo size={60} />
        )}
        <div>
          <div className="lodge-hero-name">{lodge.name}</div>
          {(lodge.city || lodge.country) && <div className="lodge-hero-city">{[lodge.city, lodge.country].filter(Boolean).join(", ")}</div>}
          {lodge.motto && <div className="lodge-hero-motto">«&nbsp;{lodge.motto}&nbsp;»</div>}
        </div>
      </div>

      {/* Accordion */}
      <div className="lodge-accordion">
        <AccordionSection icon={Eye} title={t("lodgeInfo.generalInfo")} defaultOpen>
          {INFO_FIELDS.map(([k, label]) => (
            lodge[k] ? (
              <div key={k} className="py-2" style={{ display: "flex", gap: "0.75rem" }}>
                <span style={{ fontFamily: "Cinzel, serif", color: "var(--navy-900)", fontWeight: 500 }}>{label}:</span>
                <span>{lodge[k]}</span>
              </div>
            ) : null
          ))}
          {lodge.history && (
            <p className="mt-4" style={{ whiteSpace: "pre-wrap", lineHeight: 1.75 }}>{lodge.history}</p>
          )}
        </AccordionSection>

        <AccordionSection icon={Phone} title={t("support.contact")}>
          {CONTACT_FIELDS.map(([k, label]) => (
            <div key={k} className="kv-row">
              <span className="kv-label">{label}</span>
              <span className="kv-value">{lodge[k] || "—"}</span>
            </div>
          ))}
        </AccordionSection>

        <AccordionSection icon={User} title={t("lodgeInfo.officers")}>
          {OFFICER_FIELDS.map(([k, label]) => (
            <div key={k} className="kv-row">
              <span className="kv-label">{label}</span>
              <span className="kv-value">{lodge[k] || "—"}</span>
            </div>
          ))}
        </AccordionSection>

        <AccordionSection icon={BarChart3} title={t("dashboard.membershipStats")}>
          {stats ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 py-2">
              <Stat label={t("dashboard.activeMembers")} value={stats.active_members} />
              <Stat label={t("dashboard.masterMasons")} value={stats.maestri} />
              <Stat label={t("dashboard.fellowCrafts")} value={stats.calfe} />
              <Stat label={t("dashboard.enteredApprentices")} value={stats.ucenici} />
              <Stat label={t("dashboard.honoraryMembers")} value={stats.honorary_members} />
            </div>
          ) : <div className="spinner" />}
        </AccordionSection>

        {/* ── Sovereign Branding tab — visible ONLY when the active context
              is a sub-lodge (jurisdiction lodge). The sub-lodge has its
              own stamp + WM/Secretary signatures that override the parent
              obedience's brand on every PDF (certificate, diploma, etc.). */}
        {isAdmin && childLodgeId && (
          <AccordionSection icon={StampIcon} title={t("lodgeInfo.sovereignBranding")} defaultOpen>
            <p className="kv-label" style={{ fontSize: "0.7rem", marginBottom: "0.75rem" }}>
              {t("lodgeInfo.sovereignBrandingHint")}
            </p>

            {/* Stamp */}
            <div className="brand-asset-row" data-testid="sublodge-stamp-block">
              <div className="brand-asset-label">
                <StampIcon size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
                {t("lodgeInfo.lodge_stamp_seal_private")}
              </div>
              <div className="flex items-center gap-4 mt-2">
                {lodge.stamp_url ? (
                  <img
                    src={`${API}/photo?path=${encodeURIComponent(lodge.stamp_url)}${authQuery().replace("?", "&")}`}
                    alt=""
                    style={{
                      width: 100, height: 100, objectFit: "contain",
                      border: "1px solid #c9a84c",
                      background: "repeating-conic-gradient(#f0e9d0 0% 25%, #fff 0% 50%) 50% / 12px 12px",
                      borderRadius: 6,
                    }}
                    data-testid="sublodge-stamp-preview"
                  />
                ) : (
                  <div className="brand-asset-empty">{t("lodgeInfo.no_stamp")}</div>
                )}
                <div className="flex flex-col gap-1">
                  <label className="lodge-edit-btn-gold cursor-pointer" data-testid="sublodge-stamp-upload-btn">
                    <Upload size={12} style={{ display: "inline", marginRight: "0.3rem" }} />
                    {savingBrand === "stamp"
                      ? t("lodgeInfo.vectorising")
                      : (lodge.stamp_url ? t("lodgeInfo.replace_stamp") : t("lodgeInfo.upload_stamp"))}
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={uploadBrandStamp}
                      style={{ display: "none" }}
                      disabled={savingBrand === "stamp"}
                      data-testid="sublodge-stamp-input"
                    />
                  </label>
                  {lodge.stamp_url ? (
                    <button
                      type="button"
                      className="btn-outline-gold"
                      style={{ borderColor: "#7c2a2a", color: "#7c2a2a", fontSize: "0.7rem" }}
                      onClick={removeBrandStamp}
                      data-testid="sublodge-stamp-remove-btn"
                    >
                      {t("lodgeInfo.remove")}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            {/* WM + Secretary signatures */}
            {[
              { key: "wm", label: t("lodgeInfo.wmSignature"), value: lodge.wm_signature_url, field: "wm_signature_url" },
              { key: "sec", label: t("lodgeInfo.secSignature"), value: lodge.sec_signature_url, field: "sec_signature_url" },
            ].map(({ key, label, value }) => (
              <div className="brand-asset-row" key={key} data-testid={`sublodge-${key}-sig-block`}>
                <div className="brand-asset-label">{label}</div>
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  {value ? (
                    <img
                      src={`${API}/photo?path=${encodeURIComponent(value)}${authQuery().replace("?", "&")}`}
                      alt=""
                      style={{
                        width: 200, height: 80, objectFit: "contain",
                        border: "1px solid #c9a84c", borderRadius: 6, background: "#fff",
                      }}
                      data-testid={`sublodge-${key}-sig-preview`}
                    />
                  ) : (
                    <div className="brand-asset-empty" style={{ width: 200, height: 80 }}>
                      {t("lodgeInfo.noSignature")}
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <label className="lodge-edit-btn-gold cursor-pointer" data-testid={`sublodge-${key}-sig-upload-btn`}>
                      <Upload size={12} style={{ display: "inline", marginRight: "0.3rem" }} />
                      {savingBrand === key
                        ? t("lodgeInfo.vectorising")
                        : (value ? t("lodgeInfo.replaceSignature") : t("lodgeInfo.uploadSignature"))}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={(e) => uploadBrandSignatureFile(key, e)}
                        style={{ display: "none" }}
                        disabled={savingBrand === key}
                        data-testid={`sublodge-${key}-sig-input`}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn-outline-gold"
                      onClick={() => setSigPad(key)}
                      data-testid={`sublodge-${key}-sig-draw-btn`}
                    >
                      ✍ {t("lodgeInfo.drawSignature")}
                    </button>
                    {value ? (
                      <button
                        type="button"
                        className="btn-outline-gold"
                        style={{ borderColor: "#7c2a2a", color: "#7c2a2a", fontSize: "0.7rem" }}
                        onClick={() => removeBrandSignature(key)}
                        data-testid={`sublodge-${key}-sig-remove-btn`}
                      >
                        {t("lodgeInfo.remove")}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </AccordionSection>
        )}
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="dialog-overlay" onClick={() => setEditing(false)}>
          <form onSubmit={save} className="dialog-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }} data-testid="lodge-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>{t("common.edit")}</h2>
              <button type="button" onClick={() => { setEditing(false); setForm(lodge); }}><X size={20} /></button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="label-lodge">{t("lodgeInfo.logo")}</label>
                <div className="flex items-center gap-4 mt-2">
                  {form.logo_url ? (
                    <img src={`${API}/photo?path=${encodeURIComponent(form.logo_url)}${authQuery().replace("?", "&")}`} alt="" style={{ width: 70, height: 70, objectFit: "cover", border: "1px solid #c9a84c" }} />
                  ) : (
                    <CompassLogo size={60} />
                  )}
                  <label className="lodge-edit-btn-gold cursor-pointer">
                    <Upload size={12} style={{ display: "inline", marginRight: "0.3rem" }} /> {uploading ? "..." : t("lodgeInfo.uploadLogo")}
                    <input type="file" accept="image/*" onChange={uploadLogo} style={{ display: "none" }} />
                  </label>
                </div>
              </div>

              {/* Lodge official stamp / seal — PRIVATE. Only the lodge admin
                  sees this section; it never appears on the public profile.
                  The image is auto-vectorised on the backend (white background
                  removed) and stored as a transparent PNG. */}
              <div data-testid="lodge-stamp-section">
                <label className="label-lodge">
                  {t("lodgeInfo.lodge_stamp_seal_private")}
                </label>
                <p className="kv-label" style={{ fontSize: "0.66rem", marginTop: 2, marginBottom: 8 }}>
                  {t("lodgeInfo.upload_png_jpg_pdf_the")}
                </p>
                <div className="flex items-center gap-4 mt-2">
                  {form.stamp_url ? (
                    <img
                      src={`${API}/photo?path=${encodeURIComponent(form.stamp_url)}${authQuery().replace("?", "&")}`}
                      alt="lodge stamp"
                      style={{
                        width: 100,
                        height: 100,
                        objectFit: "contain",
                        border: "1px solid #c9a84c",
                        background: "repeating-conic-gradient(#f0e9d0 0% 25%, #fff 0% 50%) 50% / 12px 12px",
                        borderRadius: 6,
                      }}
                      data-testid="lodge-stamp-preview"
                    />
                  ) : (
                    <div
                      style={{
                        width: 100, height: 100,
                        border: "1px dashed #c9a84c",
                        borderRadius: 6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#9a7530",
                        fontSize: "0.7rem",
                        fontStyle: "italic",
                      }}
                    >
                      {t("lodgeInfo.no_stamp")}
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <label className="lodge-edit-btn-gold cursor-pointer" data-testid="lodge-stamp-upload-btn">
                      <Upload size={12} style={{ display: "inline", marginRight: "0.3rem" }} />
                      {uploadingStamp
                        ? t("lodgeInfo.vectorising")
                        : (form.stamp_url
                          ? t("lodgeInfo.replace_stamp")
                          : t("lodgeInfo.upload_stamp"))}
                      <input type="file" accept="image/*,application/pdf" onChange={uploadStamp} style={{ display: "none" }} disabled={uploadingStamp} data-testid="lodge-stamp-input" />
                    </label>
                    {form.stamp_url ? (
                      <button
                        type="button"
                        className="btn-outline-gold"
                        style={{ borderColor: "#7c2a2a", color: "#7c2a2a", fontSize: "0.7rem" }}
                        onClick={removeStamp}
                        data-testid="lodge-stamp-remove-btn"
                      >
                        {t("lodgeInfo.remove")}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div>
                <div className="section-heading mb-2">{t("lodgeInfo.generalInfo")}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="label-lodge">{t("lodgeInfo.lodgeName")}</label>
                    <input className="input-lodge" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  {INFO_FIELDS.filter(([k]) => k !== "name").map(([k, label]) => (
                    <div key={k}>
                      <label className="label-lodge">{label}</label>
                      <input className="input-lodge" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                    </div>
                  ))}
                  <div className="md:col-span-2">
                    <label className="label-lodge">Motto</label>
                    <input className="input-lodge" value={form.motto || ""} onChange={(e) => setForm({ ...form, motto: e.target.value })} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-lodge">{t("lodgeInfo.history")}</label>
                    <textarea rows={5} className="input-lodge" value={form.history || ""} onChange={(e) => setForm({ ...form, history: e.target.value })} />
                  </div>
                </div>
              </div>

              <div>
                <div className="section-heading mb-2">{t("support.contact")}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {CONTACT_FIELDS.map(([k, label]) => (
                    <div key={k} className={k === "address" ? "md:col-span-2" : ""}>
                      <label className="label-lodge">{label}</label>
                      <input className="input-lodge" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="section-heading mb-2">{t("lodgeInfo.officers")}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {OFFICER_FIELDS.map(([k, label]) => (
                    <div key={k}>
                      <label className="label-lodge">{label}</label>
                      <input className="input-lodge" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => { setEditing(false); setForm(lodge); }}>{t("common.cancel")}</button>
              <button type="submit" className="btn-primary-gold" disabled={saving} data-testid="save-lodge-btn">{saving ? "..." : t("common.save")}</button>
            </div>
          </form>
        </div>
      )}
      {/* Sub-lodge sovereign signature pad (touch/mouse) */}
      {sigPad && (
        <div className="dialog-overlay" onClick={() => setSigPad(null)} data-testid="sublodge-sig-pad-dialog">
          <div className="dialog-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="dialog-title" style={{ margin: 0 }}>
                {sigPad === "wm" ? t("lodgeInfo.wmSignature") : t("lodgeInfo.secSignature")}
              </h2>
              <button type="button" onClick={() => setSigPad(null)}><X size={20} /></button>
            </div>
            <SignaturePad
              width={500}
              height={200}
              onSave={(dataUrl) => saveDrawnSignature(sigPad, dataUrl)}
              onCancel={() => setSigPad(null)}
              lang={i18n.language?.slice(0, 2) || "ro"}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ textAlign: "center", padding: "0.6rem" }}>
      <div className="tile-value" style={{ fontSize: "1.4rem" }}>{value}</div>
      <div className="kv-label" style={{ fontSize: "0.65rem" }}>{label}</div>
    </div>
  );
}
